/**
 * CANONICAL MODULE — app-platform/shared/expectations.ts
 *
 * Copied byte-for-byte into every app as `src/lib/expectations.ts`. Folded into this directory on
 * 2026-09-18 (task 2.g) along with `errorLog.ts` and `errorFlush.ts`; before that the originals
 * lived in contently and were copied out from there by hand. All three were verified identical
 * across all five apps by git blob hash at the moment of folding, tests included.
 *
 * **Fix bugs HERE, then copy outward. Never edit an app's copy in place.**
 */

// Expectation checks: the silent failures that thrown errors miss. A feed that
// quietly returns nothing, an autosave that never lands, a daily file that never
// appears: nothing throws, so errorLog.ts never hears about it. Each app declares
// a few cheap checks that run on open, at most once per calendar day each, and
// log `degraded` through the same capture path when an expectation is not met.
//
// CANONICAL MODULE, copied byte-for-byte into the sibling apps (it imports only
// errorLog.ts). The app-specific checks themselves live in healthChecks.ts next
// to this file. The one check every app shares, "the core sync path succeeded on
// none of the last N days the app was opened", is provided here.
import { reportDegraded } from './errorLog'

export interface ExpectationCheck {
  /** Stable id; also the once-a-day gate key. */
  id: string
  /** Return a one-line description of the unmet expectation, or null when fine.
   *  May be async (a check that reads IndexedDB). */
  check: () => string | null | Promise<string | null>
}

const DAY_MS = 86_400_000

/**
 * Local calendar date as YYYY-MM-DD (the user's day, not UTC's).
 * Exported for tests only (app-platform 8.3): no app code calls it.
 */
export function localDay(ms: number = Date.now()): string {
  const d = new Date(ms)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch {
    return fallback
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    /* storage unavailable: the check just has less history */
  }
}

// ------------------------------------------------------------ daily counters
// A per-day tally kept for the last `keepDays` days, e.g. how many new feed items
// each day's refreshes found. Adds to the day's existing value, so several runs
// in one day accumulate; recording 0 still creates the day's record.
export function recordDailyCount(app: string, name: string, value: number, now: number = Date.now(), keepDays = 14): void {
  const key = `${app}.errorSync.daily.${name}`
  const counts = read<Record<string, number>>(key, {})
  const day = localDay(now)
  counts[day] = (typeof counts[day] === 'number' ? counts[day] : 0) + value
  const cutoff = localDay(now - keepDays * DAY_MS)
  for (const d of Object.keys(counts)) if (d < cutoff) delete counts[d]
  write(key, counts)
}

export function readDailyCounts(app: string, name: string): Record<string, number> {
  const counts = read<unknown>(`${app}.errorSync.daily.${name}`, {})
  if (!counts || typeof counts !== 'object') return {}
  const out: Record<string, number> = {}
  for (const [d, v] of Object.entries(counts as Record<string, unknown>)) if (typeof v === 'number') out[d] = v
  return out
}

/** The `n` most recent recorded days, newest first; empty when fewer than `n` exist. */
export function recentDays(counts: Record<string, number>, n: number): string[] {
  const days = Object.keys(counts).sort().reverse()
  return days.length >= n ? days.slice(0, n) : []
}

/** True when there are at least `n` recorded days and the most recent `n` are all zero.
 *  Days the app was never opened leave no record and are not counted as zero. */
export function lastDaysAllZero(counts: Record<string, number>, n: number): boolean {
  const days = recentDays(counts, n)
  return days.length === n && days.every((d) => counts[d] === 0)
}

// ------------------------------------------------------------ shared sync check
// "Did the core sync path do anything lately": every app records the day it was
// opened (value 0, so the day exists) and every successful OneDrive sync (+1).
// Three opened days in a row with no successful sync is the silent-sync-failure
// shape, whatever the app. Run the check AFTER the day's first sync attempt has
// settled, so a day that is about to succeed is not counted as a zero.
/** The daily-counter key the silent-sync detector reads. **Exported deliberately**: trakr reached
 *  for this string by copying the literal into its own `healthChecks.ts`, and the two agreed only
 *  because the copy happened to be right — nothing checked it (audit 1.1, accident 2). Import it. */
export const SYNC_OK = 'syncOk'

/** Opened days in a row with no successful sync before the silent-sync check fires.
 *  Three, because it has to survive a weekend away from a device without crying wolf, and two would
 *  not. The number appeared undocumented in four apps before this comment (audit 1.1, accident 3). */
const SYNC_SILENT_DAYS = 3

export function noteAppOpened(app: string, now: number = Date.now()): void {
  recordDailyCount(app, SYNC_OK, 0, now)
}

export function noteSyncSuccess(app: string, now: number = Date.now()): void {
  recordDailyCount(app, SYNC_OK, 1, now)
}

export function syncSilentCheck(app: string, days = SYNC_SILENT_DAYS): ExpectationCheck {
  return {
    id: 'sync-silent',
    check: () => {
      const counts = readDailyCounts(app, SYNC_OK)
      if (!lastDaysAllZero(counts, days)) return null
      const when = recentDays(counts, days).reverse().join(', ')
      return `no successful OneDrive sync on the last ${days} days the app was opened (${when})`
    },
  }
}

// ------------------------------------------------------------ the runner
/** Run each check at most once per local calendar day, logging `degraded` for
 *  any unmet expectation. A check that throws or rejects is skipped for today
 *  (never surfaces as a crash; the reporter must not become a source of errors). */
export function runExpectationChecks(app: string, checks: ExpectationCheck[], now: number = Date.now()): void {
  const today = localDay(now)
  for (const c of checks) {
    const key = `${app}.errorSync.checked.${c.id}`
    try {
      if (read<string | null>(key, null) === today) continue
      write(key, today)
      const out = c.check()
      const settle = (problem: string | null) => {
        if (problem) reportDegraded(problem, `check:${c.id}`)
      }
      if (out && typeof out === 'object' && typeof (out as Promise<string | null>).then === 'function') {
        void (out as Promise<string | null>).then(settle).catch(() => undefined)
      } else {
        settle(out as string | null)
      }
    } catch {
      /* skipped today */
    }
  }
}
